import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';

const Badge = ({ status }) => {
  const colors = {
    approved: {
      bg:'rgba(0,229,160,0.1)',
      color:'var(--green)',
      border:'rgba(0,229,160,0.3)'
    },
    pending: {
      bg:'rgba(255,192,64,0.1)',
      color:'var(--yellow)',
      border:'rgba(255,192,64,0.3)'
    },
    rejected: {
      bg:'rgba(255,77,109,0.1)',
      color:'var(--red)',
      border:'rgba(255,77,109,0.3)'
    },
    suspended: {
      bg:'rgba(77,95,122,0.2)',
      color:'var(--text3)',
      border:'rgba(77,95,122,0.3)'
    },
  };
  const c = colors[status] || colors.pending;
  return (
    <span style={{
      background: c.bg,
      color: c.color,
      border: `1px solid ${c.border}`,
      padding:'3px 10px',
      borderRadius:'20px',
      fontSize:'11px',
      fontFamily:'DM Mono,monospace',
      textTransform:'capitalize',
    }}>
      {status}
    </span>
  );
};

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] =
    useState('');
  const [showDetail, setShowDetail] =
    useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    loadVendors();
  }, [filter]);

  const loadVendors = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') {
        params.status = filter;
      }
      if (search) params.search = search;
      const res = await adminAPI.getVendors(params);
      setVendors(res.data.data);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await adminAPI.getVendorDetail(id);
      setDetail(res.data.data);
      setShowDetail(true);
    } catch (err) {
      alert('Failed to load vendor details');
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id + '_approve');
    try {
      await adminAPI.approveVendor(id);
      await loadVendors();
      if (showDetail) await loadDetail(id);
      alert('Vendor approved successfully!');
    } catch (err) {
      alert('Failed to approve vendor');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async (id) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;
    setActionLoading(id + '_reject');
    try {
      await adminAPI.rejectVendor(id, { reason });
      await loadVendors();
      setShowDetail(false);
      alert('Vendor rejected.');
    } catch (err) {
      alert('Failed to reject vendor');
    } finally {
      setActionLoading('');
    }
  };

  const handleSuspend = async (id) => {
    const reason = prompt('Enter suspension reason:');
    if (!reason) return;
    setActionLoading(id + '_suspend');
    try {
      await adminAPI.suspendVendor(id, { reason });
      await loadVendors();
      setShowDetail(false);
      alert('Vendor suspended.');
    } catch (err) {
      alert('Failed to suspend vendor');
    } finally {
      setActionLoading('');
    }
  };

  const handleReactivate = async (id) => {
    setActionLoading(id + '_reactivate');
    try {
      await adminAPI.reactivateVendor(id);
      await loadVendors();
      if (showDetail) await loadDetail(id);
      alert('Vendor reactivated!');
    } catch (err) {
      alert('Failed to reactivate vendor');
    } finally {
      setActionLoading('');
    }
  };

  const filterButtons = [
    { key:'all', label:'All' },
    { key:'pending', label:'Pending' },
    { key:'approved', label:'Approved' },
    { key:'rejected', label:'Rejected' },
    { key:'suspended', label:'Suspended' },
  ];

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        marginBottom:'24px',
      }}>
        <h1 style={{
          fontFamily:'Syne,sans-serif',
          fontSize:'22px', fontWeight:'800',
        }}>
          Vendor Management
        </h1>
        <p style={{
          color:'var(--text3)',
          fontSize:'13px', marginTop:'4px',
        }}>
          Manage all registered vendors
          on IRONWALL Service Bridge
        </p>
      </div>

      {/* Filters */}
      <div style={{
        display:'flex', gap:'10px',
        marginBottom:'20px',
        flexWrap:'wrap', alignItems:'center',
      }}>
        <div style={{
          display:'flex', gap:'6px',
        }}>
          {filterButtons.map(f => (
            <button key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding:'7px 16px',
                borderRadius:'20px',
                border: filter === f.key
                  ? 'none'
                  : '1px solid var(--border2)',
                background: filter === f.key
                  ? 'var(--accent)'
                  : 'transparent',
                color: filter === f.key
                  ? 'var(--bg)'
                  : 'var(--text2)',
                fontSize:'12px',
                fontWeight:'600',
                cursor:'pointer',
                transition:'all 0.15s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div style={{
          display:'flex',
          alignItems:'center',
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          borderRadius:'8px',
          padding:'0 12px',
          gap:'8px', flex:1,
          maxWidth:'300px',
        }}>
          <span style={{
            color:'var(--text3)',
            fontSize:'14px',
          }}>⊞</span>
          <input
            type="text"
            placeholder="Search vendors..."
            value={search}
            onChange={e =>
              setSearch(e.target.value)}
            onKeyPress={e =>
              e.key === 'Enter' && loadVendors()}
            style={{
              background:'none',
              border:'none', outline:'none',
              color:'var(--text)',
              fontSize:'13px',
              padding:'9px 0', flex:1,
            }}
          />
        </div>

        <button onClick={loadVendors} style={{
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          color:'var(--text2)',
          padding:'8px 16px',
          borderRadius:'8px',
          fontSize:'13px',
          cursor:'pointer',
        }}>
          Search
        </button>
      </div>

      {/* Main Layout */}
      <div style={{
        display:'grid',
        gridTemplateColumns:
          showDetail ? '1fr 380px' : '1fr',
        gap:'16px',
      }}>
        {/* Vendors Table */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          {loading ? (
            <div style={{
              padding:'40px',
              textAlign:'center',
              color:'var(--text3)',
              fontFamily:'DM Mono,monospace',
            }}>
              Loading vendors...
            </div>
          ) : vendors.length === 0 ? (
            <div style={{
              padding:'60px',
              textAlign:'center',
              color:'var(--text3)',
            }}>
              <div style={{
                fontSize:'40px',
                marginBottom:'12px',
              }}>
                ◈
              </div>
              <div style={{
                fontSize:'14px',
                color:'var(--text2)',
                marginBottom:'6px',
              }}>
                No vendors found
              </div>
              <div style={{ fontSize:'12px' }}>
                {filter !== 'all'
                  ? `No ${filter} vendors`
                  : 'No vendors registered yet'}
              </div>
            </div>
          ) : (
            <table style={{
              width:'100%',
              borderCollapse:'collapse',
            }}>
              <thead>
                <tr>
                  {[
                    'Business Name','Category',
                    'Location','Engineers',
                    'Phone','Status',
                    'Registered','Actions'
                  ].map(h => (
                    <th key={h} style={{
                      padding:'10px 16px',
                      textAlign:'left',
                      fontSize:'10px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text3)',
                      letterSpacing:'1.5px',
                      textTransform:'uppercase',
                      borderBottom:
                        '1px solid var(--border)',
                      background:
                        'var(--surface2)',
                      whiteSpace:'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vendors.map(v => (
                  <tr key={v.id}
                    style={{
                      borderBottom:
                        '1px solid var(--border)',
                      cursor:'pointer',
                      background:
                        selected === v.id
                          ? 'rgba(0,212,255,0.05)'
                          : 'transparent',
                    }}
                    onClick={() => {
                      setSelected(v.id);
                      loadDetail(v.id);
                    }}
                  >
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <div style={{
                        fontWeight:'600',
                        fontSize:'13px',
                      }}>
                        {v.business_name}
                      </div>
                      <div style={{
                        fontSize:'11px',
                        color:'var(--text3)',
                        marginTop:'2px',
                      }}>
                        {v.owner_name}
                      </div>
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <span style={{
                        background:
                          'rgba(0,212,255,0.08)',
                        color:'var(--accent)',
                        padding:'3px 8px',
                        borderRadius:'4px',
                        fontSize:'11px',
                        fontFamily:
                          'DM Mono,monospace',
                      }}>
                        {v.category_name
                          || 'General'}
                      </span>
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'13px',
                      color:'var(--text2)',
                    }}>
                      {v.city}, {v.state}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'13px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--accent)',
                      textAlign:'center',
                    }}>
                      {v.active_engineers}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'12px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text2)',
                    }}>
                      {v.phone}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <Badge status={v.status} />
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'11px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text3)',
                    }}>
                      {new Date(v.created_at)
                        .toLocaleDateString('en-IN')}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <div style={{
                        display:'flex',
                        gap:'6px',
                      }}>
                        {v.status === 'pending' && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleApprove(v.id);
                            }}
                            style={{
                              background:
                                'rgba(0,229,160,0.15)',
                              color:'var(--green)',
                              border:
                                '1px solid rgba(0,229,160,0.3)',
                              padding:'4px 10px',
                              borderRadius:'6px',
                              fontSize:'11px',
                              fontWeight:'600',
                              cursor:'pointer',
                              whiteSpace:'nowrap',
                            }}
                          >
                            ✓ Approve
                          </button>
                        )}
                        {v.status === 'approved' && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleSuspend(v.id);
                            }}
                            style={{
                              background:
                                'rgba(255,77,109,0.1)',
                              color:'var(--red)',
                              border:
                                '1px solid rgba(255,77,109,0.3)',
                              padding:'4px 10px',
                              borderRadius:'6px',
                              fontSize:'11px',
                              fontWeight:'600',
                              cursor:'pointer',
                            }}
                          >
                            Suspend
                          </button>
                        )}
                        {(v.status === 'suspended'
                          || v.status === 'rejected')
                          && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              handleReactivate(v.id);
                            }}
                            style={{
                              background:
                                'rgba(0,212,255,0.1)',
                              color:'var(--accent)',
                              border:
                                '1px solid rgba(0,212,255,0.3)',
                              padding:'4px 10px',
                              borderRadius:'6px',
                              fontSize:'11px',
                              fontWeight:'600',
                              cursor:'pointer',
                            }}
                          >
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Vendor Detail Panel */}
        {showDetail && detail && (
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
            height:'fit-content',
          }}>
            {/* Detail Header */}
            <div style={{
              padding:'16px 20px',
              borderBottom:
                '1px solid var(--border)',
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
            }}>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'14px',
                fontWeight:'700',
              }}>
                Vendor Details
              </div>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setSelected(null);
                }}
                style={{
                  background:'none',
                  border:'none',
                  color:'var(--text3)',
                  cursor:'pointer',
                  fontSize:'18px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding:'20px' }}>
              {/* Business Info */}
              <div style={{
                marginBottom:'20px',
              }}>
                <div style={{
                  fontFamily:'Syne,sans-serif',
                  fontSize:'16px',
                  fontWeight:'700',
                  marginBottom:'4px',
                }}>
                  {detail.vendor.business_name}
                </div>
                <div style={{
                  fontSize:'12px',
                  color:'var(--text3)',
                  marginBottom:'12px',
                }}>
                  {detail.vendor.owner_name}
                </div>
                <Badge
                  status={detail.vendor.status}
                />
              </div>

              {/* Info Grid */}
              {[
                ['Email', detail.vendor.email],
                ['Phone', detail.vendor.phone],
                ['City', `${detail.vendor.city}, ${detail.vendor.state}`],
                ['Pincode', detail.vendor.pincode],
                ['Category', detail.vendor.category_name],
                ['GST', detail.vendor.gst_number || 'Not provided'],
                ['Engineers', detail.vendor.num_engineers],
                ['Plan', detail.vendor.subscription_plan],
              ].map(([label, value]) => (
                <div key={label} style={{
                  display:'flex',
                  justifyContent:'space-between',
                  padding:'8px 0',
                  borderBottom:
                    '1px solid var(--border)',
                  fontSize:'12.5px',
                }}>
                  <span style={{
                    color:'var(--text3)',
                    fontFamily:
                      'DM Mono,monospace',
                    fontSize:'11px',
                  }}>
                    {label}
                  </span>
                  <span style={{
                    color:'var(--text)',
                    fontWeight:'500',
                    textAlign:'right',
                    maxWidth:'180px',
                    wordBreak:'break-all',
                  }}>
                    {value}
                  </span>
                </div>
              ))}

              {/* Service Credits */}
              {detail.service_credits?.length > 0 && (
                <div style={{
                  marginTop:'16px',
                  padding:'12px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    marginBottom:'8px',
                    letterSpacing:'1px',
                  }}>
                    SERVICE CALL CREDITS
                  </div>
                  {detail.service_credits
                    .map(c => (
                    <div key={c.id} style={{
                      display:'flex',
                      justifyContent:
                        'space-between',
                      fontSize:'13px',
                    }}>
                      <span style={{
                        color:'var(--text2)',
                      }}>
                        {c.year}
                      </span>
                      <span>
                        <span style={{
                          color:'var(--red)',
                        }}>
                          {c.used}
                        </span>
                        <span style={{
                          color:'var(--text3)',
                        }}>
                          {' '}used /{' '}
                        </span>
                        <span style={{
                          color:'var(--green)',
                        }}>
                          {c.quota}
                        </span>
                        <span style={{
                          color:'var(--text3)',
                        }}>
                          {' '}quota
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Engineers */}
              <div style={{ marginTop:'16px' }}>
                <div style={{
                  fontSize:'11px',
                  fontFamily:'DM Mono,monospace',
                  color:'var(--text3)',
                  letterSpacing:'1px',
                  marginBottom:'8px',
                }}>
                  ENGINEERS (
                  {detail.engineers.length})
                </div>
                {detail.engineers.length === 0 ? (
                  <div style={{
                    fontSize:'12px',
                    color:'var(--text3)',
                  }}>
                    No engineers added yet
                  </div>
                ) : (
                  detail.engineers.map(e => (
                    <div key={e.id} style={{
                      display:'flex',
                      justifyContent:
                        'space-between',
                      alignItems:'center',
                      padding:'8px 0',
                      borderBottom:
                        '1px solid var(--border)',
                      fontSize:'12.5px',
                    }}>
                      <div>
                        <div style={{
                          fontWeight:'600',
                        }}>
                          {e.name}
                        </div>
                        <div style={{
                          fontSize:'11px',
                          color:'var(--text3)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {e.phone}
                        </div>
                      </div>
                      <span style={{
                        fontSize:'10px',
                        padding:'2px 8px',
                        borderRadius:'10px',
                        background:
                          e.current_status ===
                          'active'
                            ? 'rgba(0,229,160,0.1)'
                            : 'rgba(77,95,122,0.2)',
                        color:
                          e.current_status ===
                          'active'
                            ? 'var(--green)'
                            : 'var(--text3)',
                      }}>
                        {e.current_status}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Action Buttons */}
              <div style={{
                marginTop:'20px',
                display:'flex',
                flexDirection:'column',
                gap:'8px',
              }}>
                {detail.vendor.status ===
                  'pending' && (
                  <>
                    <button
                      onClick={() =>
                        handleApprove(
                          detail.vendor.id)}
                      style={{
                        background:
                          'var(--green)',
                        color:'var(--bg)',
                        border:'none',
                        padding:'10px',
                        borderRadius:'8px',
                        fontSize:'13px',
                        fontWeight:'700',
                        cursor:'pointer',
                      }}
                    >
                      ✓ Approve Vendor
                    </button>
                    <button
                      onClick={() =>
                        handleReject(
                          detail.vendor.id)}
                      style={{
                        background:
                          'rgba(255,77,109,0.15)',
                        color:'var(--red)',
                        border:
                          '1px solid rgba(255,77,109,0.3)',
                        padding:'10px',
                        borderRadius:'8px',
                        fontSize:'13px',
                        fontWeight:'600',
                        cursor:'pointer',
                      }}
                    >
                      ✕ Reject Vendor
                    </button>
                  </>
                )}
                {detail.vendor.status ===
                  'approved' && (
                  <button
                    onClick={() =>
                      handleSuspend(
                        detail.vendor.id)}
                    style={{
                      background:
                        'rgba(255,77,109,0.15)',
                      color:'var(--red)',
                      border:
                        '1px solid rgba(255,77,109,0.3)',
                      padding:'10px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'600',
                      cursor:'pointer',
                    }}
                  >
                    Suspend Vendor
                  </button>
                )}
                {(detail.vendor.status ===
                  'suspended' ||
                  detail.vendor.status ===
                  'rejected') && (
                  <button
                    onClick={() =>
                      handleReactivate(
                        detail.vendor.id)}
                    style={{
                      background:
                        'var(--accent)',
                      color:'var(--bg)',
                      border:'none',
                      padding:'10px',
                      borderRadius:'8px',
                      fontSize:'13px',
                      fontWeight:'700',
                      cursor:'pointer',
                    }}
                  >
                    Reactivate Vendor
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorManagement;